import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, Search, X, Phone, ChevronDown } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import SearchDialog from "./SearchDialog";
import { useSite } from "./SiteContext";
import { destUrl } from "../lib/api";

const LINKS = [
  { label: "International", to: "/international", key: "international" },
  { label: "India", to: "/india", key: "india" },
  { label: "Experiences", to: "/experiences", key: "experiences" },
  { label: "Journeys", to: "/journeys", key: "journeys" },
  { label: "Corporate", to: "/corporate" },
  { label: "Visa", to: "/visa", key: "visa_pages" },
  { label: "Travel Styles", to: "/travel-styles", key: "travel_styles" },
  { label: "Blog", to: "/blog" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

const navLinkClass = (solid, isActive) => {
  if (solid) return isActive ? "text-[var(--brass-2)]" : "text-[var(--ink-2)] hover:text-[var(--brass-2)]";
  return isActive ? "text-[#E4CFA9]" : "text-white/85 hover:text-white";
};

const MEGA_PATHS = {
  experiences: "/experiences",
  journeys: "/journeys",
  visa_pages: "/visa",
};
const megaItemHref = (sectionKey, item) => {
  if (sectionKey === "international" || sectionKey === "india") return destUrl(item);
  const base = MEGA_PATHS[sectionKey] || "/travel-styles";
  return `${base}/${item.slug}`;
};

export default function Nav() {
  const { settings, nav } = useSite();
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);

  const onHero = pathname === "/";
  const solid = scrolled || !onHero;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenMenu(null);
  }, [pathname]);

  const panelFor = (key) => {
    const items = nav?.[key] || [];
    if (!items.length) return null;
    return items.slice(0, 12);
  };

  return (
    <>
      <header
        data-testid="primary-nav"
        className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
          solid
            ? "border-b border-[var(--line)] bg-[rgba(251,247,239,0.94)] backdrop-blur-md"
            : "bg-gradient-to-b from-[rgba(10,16,22,0.55)] to-transparent"
        }`}
        onMouseLeave={() => setOpenMenu(null)}
      >
        <div className="az-container flex h-[68px] items-center justify-between gap-4 lg:h-[76px]">
          <Link to="/" data-testid="nav-logo-link" className="flex shrink-0 items-center">
            <img
              src={solid ? "/azulo-logo.webp" : "/azulo-logo-white.webp"}
              alt="Azulo Travel — Curated Journeys. Trusted Hospitality."
              width={2000}
              height={571}
              className="h-9 w-auto transition-opacity duration-200 lg:h-10"
            />
          </Link>

          <nav className="hidden items-center gap-1 xl:flex">
            {LINKS.map((l) => {
              const items = l.key ? panelFor(l.key) : null;
              return (
                <div key={l.to} className="relative" onMouseEnter={() => setOpenMenu(l.key ? l.to : null)}>
                  <NavLink
                    to={l.to}
                    data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) =>
                      `flex items-center gap-1 whitespace-nowrap rounded-lg px-2.5 py-2 text-[0.8rem] font-medium transition-colors ${navLinkClass(
                        solid,
                        isActive
                      )}`
                    }
                  >
                    {l.label}
                    {items && <ChevronDown className="h-3 w-3 opacity-60" />}
                  </NavLink>
                  {items && openMenu === l.to && (
                    <div className="absolute left-0 top-full z-50 w-[560px] rounded-2xl border border-[var(--line)] bg-[var(--surface)] p-4 shadow-[0_24px_60px_rgba(14,30,42,0.16)]">
                      <div className="grid grid-cols-2 gap-1">
                        {items.map((it) => (
                          <Link
                            key={it.slug}
                            to={megaItemHref(l.key, it)}
                            data-testid={`nav-mega-item-${it.slug}`}
                            className="flex items-center gap-3 rounded-lg p-2 hover:bg-[var(--paper-2)]"
                          >
                            {(it.card_image || it.hero_image) && (
                              <img
                                src={it.card_image || it.hero_image}
                                alt=""
                                loading="lazy"
                                className="h-9 w-12 shrink-0 rounded object-cover"
                              />
                            )}
                            <span className="truncate text-[0.82rem] text-[var(--ink-2)]">
                              {it.flag_emoji ? `${it.flag_emoji} ` : ""}
                              {it.title}
                            </span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              aria-label="Search"
              data-testid="universal-search-open-button"
              className={`rounded-full p-2.5 transition-colors ${
                solid ? "text-[var(--ink-2)] hover:bg-[var(--paper-2)]" : "text-white hover:bg-white/15"
              }`}
            >
              <Search className="h-[1.05rem] w-[1.05rem]" />
            </button>
            <a
              href={`tel:${(settings.phone || "").replace(/\s/g, "")}`}
              data-testid="nav-phone-link"
              className={`hidden items-center gap-2 whitespace-nowrap text-xs font-semibold lg:flex ${
                solid ? "text-[var(--ink-2)]" : "text-white/90"
              }`}
            >
              <Phone className="h-3.5 w-3.5" />
              {settings.phone}
            </a>
            <Link
              to="/plan-my-journey"
              data-testid="nav-plan-my-journey-button"
              className="hidden whitespace-nowrap rounded-xl bg-[var(--brass)] px-4 py-2.5 text-xs font-semibold text-white transition-colors hover:bg-[var(--brass-2)] sm:block"
            >
              Plan My Journey
            </Link>

            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button
                  type="button"
                  aria-label="Open menu"
                  data-testid="mobile-nav-open-button"
                  className={`rounded-full p-2.5 xl:hidden ${
                    solid ? "text-[var(--ink)]" : "text-white"
                  }`}
                >
                  <Menu className="h-5 w-5" />
                </button>
              </SheetTrigger>
              <SheetContent
                side="right"
                className="w-full max-w-sm overflow-y-auto border-l border-[var(--line)] bg-[var(--paper)] p-0"
              >
                <div className="flex items-center justify-between border-b border-[var(--line)] px-5 py-4">
                  <span className="font-display text-lg tracking-[0.14em] text-[var(--ink)]">
                    <img src="/azulo-logo.webp" alt="Azulo Travel" className="h-8 w-auto" />
                  </span>
                  <button
                    type="button"
                    onClick={() => setMobileOpen(false)}
                    aria-label="Close menu"
                    data-testid="mobile-nav-close-button"
                    className="rounded-full p-2 text-[var(--ink-2)]"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <nav className="flex flex-col px-2 py-3">
                  {LINKS.map((l) => (
                    <NavLink
                      key={l.to}
                      to={l.to}
                      data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className={({ isActive }) =>
                        `rounded-xl px-4 py-3.5 font-display text-xl ${
                          isActive ? "text-[var(--brass-2)]" : "text-[var(--ink)]"
                        }`
                      }
                    >
                      {l.label}
                    </NavLink>
                  ))}
                </nav>
                <div className="border-t border-[var(--line)] p-5">
                  <Link
                    to="/plan-my-journey"
                    data-testid="mobile-nav-plan-button"
                    className="block rounded-xl bg-[var(--brass)] px-5 py-3.5 text-center text-sm font-semibold text-white"
                  >
                    Plan My Journey
                  </Link>
                  <a
                    href={`tel:${(settings.phone || "").replace(/\s/g, "")}`}
                    className="mt-3 block rounded-xl border border-[var(--line-strong)] bg-[var(--surface)] px-5 py-3.5 text-center text-sm font-semibold text-[var(--ink)]"
                  >
                    {settings.phone}
                  </a>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>
      <SearchDialog open={searchOpen} setOpen={setSearchOpen} />
    </>
  );
}
